import React, {useEffect, useState} from "react";
import {useChangeStatusUser, useFindUserKeycloak, useListAllUsers} from "../../services/admin-user/apiUsers";
import {UserInterface} from "../../services/interface/UserInterface";
import CustomTable, {TypeItemTable} from "../../components/general/CustomTable";
import {useTranslation} from "react-i18next";
import CustomButton from "../../components/general/CustomButton";
import DialogCreateUser from "./DialogCreateUser";
import DialogPermission from "./DialogPermission";
import {IconButton, Spinner, Tooltip} from "@material-tailwind/react";
import * as Icons from "@mui/icons-material";
import {showToast} from "../../components/general/CustomToast";
import {UserKeycloakInterface} from "../../services/interface/UserKeycloakInterface";

export default function AdminUser() {
    const {t} = useTranslation("global") as { t: (key: string) => string }

    const {refetch: findAllUser, isPending, data} = useListAllUsers()
    const {
        mutate: findUserKeycloak,
        isPending: isLoadingFindUserKeycloak,
        data: successFindUserKeycloak
    } = useFindUserKeycloak()
    const {mutate: changeStatus, isPending: isLoadingChange, data: successChangeUser} = useChangeStatusUser()

    const [title, setTitle] = useState(t('create-user'))
    const [open, setOpen] = useState(false)
    const [openPermission, setOpenPermission] = useState(false)

    const [user, setUser] = React.useState<UserInterface | undefined>()
    const [list, setList] = React.useState<UserInterface[] | undefined>()
    const [listElement, setListElement] = useState<TypeItemTable[] | undefined>()

    const [action, setAction] = React.useState<string | undefined>()
    const [userKeycloak, setUserKeycloak] = React.useState<UserKeycloakInterface | undefined>()

    useEffect(() => {
        findAllUser({});
    }, []);

    useEffect(() => {
        if (data && data.length > 0) {
            setList(data)
        }
    }, [data]);

    useEffect(() => {
        if (action === t('delete') && userKeycloak) {
            showToast(`${t('confirm-inactive-user')} ${user?.username}`, 'warning', () => {
                changeStatus({user: user, userKeycloak: {...userKeycloak, enabled: false}})
            })
        }
        if (action === t('active-user') && userKeycloak) {
            showToast(`${t('confirm-active-user')} ${user?.username}`, 'warning', () => {
                changeStatus({user: user, userKeycloak: {...userKeycloak, enabled: true}})
            })
        }
        if (action === t('create-user') && userKeycloak) {
            setOpen(true)
        }
    }, [action, userKeycloak]);

    useEffect(() => {
        if (successFindUserKeycloak) {
            setUserKeycloak(successFindUserKeycloak);
        }
    }, [successFindUserKeycloak]);

    useEffect(() => {
        if (successChangeUser) {
            setUserKeycloak(undefined)
            setUser(undefined)
            setAction(undefined)
        }
    }, [successChangeUser]);

    useEffect(() => {
        if (list) {
            setListElement(list.map((item, index) => ({
                row: [
                    item.username,
                    item.nombre,
                    item.apellido,
                    item.rolDescripcion,
                    item.celular,
                    item.correo,
                    item.estado === 1 ? 'ACTIVO' : 'INACTIVO',
                    <div className={'flex justify-center gap-2 py-1 px-1'}>
                        <IconButton key={`key-permission-${index}`} placeholder="icon-button" variant="text"
                                    color="blue"
                                    onClick={() => {
                                        setUser(item)
                                        setOpenPermission(true)
                                    }}>
                            <Icons.GroupAdd className="w-5 text-gray-600"/>
                        </IconButton>
                        <IconButton key={`key-edit-${index}`} placeholder="icon-button" variant="text"
                                    color="blue"
                                    onClick={() => {
                                        findUserKeycloak({username: item.username})
                                        setTitle(t('edit-user'))
                                        setAction(t('edit-user'))
                                        setUser(item)
                                        setOpen(true)
                                    }}>
                            <Icons.ModeEdit className="w-5 text-gray-600"/>
                        </IconButton>
                        <Tooltip content={item.estado === 1 ? t('inactive-user') : t('active-user')} placement="top">
                            <IconButton key={`key-inactive-${index}`} placeholder="icon-button" variant="text"
                                        color="blue"
                                        onClick={() => {
                                            setAction(item.estado === 1 ? t('delete') : t('active-user'));
                                            setUser(item)
                                            setUserKeycloak(undefined)
                                            findUserKeycloak({username: item.username})
                                        }}>
                                {
                                    (isLoadingChange || isLoadingFindUserKeycloak) ?
                                        <Spinner/> :
                                        <>
                                            {item.estado === 2 ?
                                                <Icons.GppBadTwoTone className="w-5 text-danger"/> :
                                                <Icons.GppGoodTwoTone className="w-5 text-success"/>
                                            }
                                        </>
                                }
                            </IconButton>
                        </Tooltip>
                    </div>,
                ]
            })))
        }
    }, [list]);

    return (
        <div className={'flex flex-col md:px-20 pt-6 gap-3'}>
            <DialogPermission open={openPermission} setOpen={setOpenPermission} {...{user}}/>
            <DialogCreateUser userDB={user} {...{open, setOpen, title, userKeycloak}}/>
            <div className={'flex flex-row items-center'}>
                <h3>{t('admin-user')}</h3>
                <CustomButton
                    className={'ml-auto bg-primary'}
                    text={t('create-user')}
                    onClick={() => {
                        setUserKeycloak(undefined)
                        setUser(undefined)
                        setTitle(t('create-user'))
                        setOpen(true)
                    }}
                />
            </div>
            <CustomTable
                loading={isPending}
                highTable={'552px'}
                header={[
                    t('username'),
                    t('name'),
                    t('lastname'),
                    t('rol'),
                    t('phone'),
                    t('email'),
                    t('status'),
                    t('action'),
                ]}
                {...{listElement}}
            />
        </div>
    )
}
